import { useRef, useState, type ReactNode } from 'react';
import { AlertCircle, FileText, Loader2 } from 'lucide-react';
import { BRAND_IMPORT_PROGRESS_TEXT, summarizeImportDraft, type BrandImportJobView } from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { brandMatchText, importFailureText, importSummaryText, sourcesBannerText } from '../../brand/importSummary';
import { isImportActive } from '../../brand/queries';
import { IMPORT_BANNER_COPY } from '../wizardCopy';
import { NotImportedDialog } from './NotImportedDialog';

const BOX = 'flex items-start gap-3 rounded-xl border px-4 py-3 text-sm';
const NEUTRAL = 'border-line bg-card text-ink';
const FAILED = 'border-red-200 bg-red-50 text-red-700 dark:border-error-dark/30 dark:bg-error-dark-tint dark:text-error-dark';

/**
 * The import's banner on steps 2-8 in upload mode (spec:93-95, :342, :351, :381-386; web.md §5.5):
 * - running: the progress sentence; the fields stay editable and fill in as results arrive;
 * - read: "Filled in from {document}. Tags above each field show where its content came from.", the
 *   summary with "See what wasn't imported", and the multi-brand notice when no brand in the document
 *   matched the workspace's name;
 * - failed: why, with Try again after an AI failure, or Choose another file (back to step 1).
 * The running and read states share one polite live region; a failure is an alert.
 */
export function ImportBanner({
  job,
  timedOut,
  workspaceName,
  overflow,
  retrying,
  retryError,
  onRetry,
  onChooseAnotherFile,
}: {
  job: BrandImportJobView;
  timedOut: boolean;
  workspaceName: string;
  /** Imported audiences past the limit of 10. */
  overflow: readonly string[];
  retrying: boolean;
  retryError: string | null;
  onRetry: () => void;
  onChooseAnotherFile: () => void;
}) {
  const [listOpen, setListOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);

  const running = isImportActive(job) && !timedOut;
  const failed = timedOut || job.status === 'failed';
  const sources = running || failed ? null : sourcesBannerText(job);
  if (!running && !failed && sources === null) return null;

  let status: ReactNode = null;
  if (running) {
    status = (
      <div className={`${BOX} ${NEUTRAL}`}>
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-ink-muted motion-reduce:animate-none" aria-hidden />
        <p>{BRAND_IMPORT_PROGRESS_TEXT}</p>
      </div>
    );
  } else if (sources !== null) {
    const base = job.summary ?? (job.draft ? summarizeImportDraft(job.draft, job.notImported) : null);
    // The audiences that did not fit beside the workspace's own are "not imported" as well.
    const summary = base ? { ...base, notImportedSections: base.notImportedSections + overflow.length } : null;
    const notImported = job.notImported.length + overflow.length;
    const pagesFailed = job.pages.filter((page) => page.status === 'failed').length;
    const match = brandMatchText(job.brandMatch, workspaceName);
    status = (
      <div className={`${BOX} ${NEUTRAL}`}>
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-teal" aria-hidden />
        <div className="min-w-0 space-y-1">
          <p>{sources}</p>
          {pagesFailed > 0 && <p>{IMPORT_BANNER_COPY.pagesFailed(pagesFailed, job.pages.length)}</p>}
          {summary && (
            <p className="text-ink-muted">
              {importSummaryText(summary)}
              {notImported > 0 && (
                <>
                  {' '}
                  <button
                    ref={opener}
                    type="button"
                    onClick={() => setListOpen(true)}
                    className="focus-ring rounded font-medium text-link underline-offset-2 hover:underline"
                  >
                    {IMPORT_BANNER_COPY.seeNotImported}
                  </button>
                </>
              )}
            </p>
          )}
          {match && <p className="text-ink-strong">{match}</p>}
        </div>
      </div>
    );
  }

  const code = timedOut ? 'ai_failed' : (job.error?.code ?? 'ai_failed');
  // Try again after an AI failure the server still allows; a file it cannot read needs another file.
  const canRetry = timedOut || (code === 'ai_failed' && job.error?.canRetry !== false);

  return (
    <div>
      <div role="status">{status}</div>
      {failed && (
        <div role="alert" className={`${BOX} ${FAILED}`}>
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 space-y-2">
            <p>{importFailureText(code)}</p>
            {retryError && <p>{retryError}</p>}
          </div>
          {canRetry ? (
            <Button type="button" variant="secondary" size="sm" className="shrink-0" loading={retrying} onClick={onRetry}>
              {IMPORT_BANNER_COPY.tryAgain}
            </Button>
          ) : (
            <Button type="button" variant="secondary" size="sm" className="shrink-0" onClick={onChooseAnotherFile}>
              {IMPORT_BANNER_COPY.chooseAnother}
            </Button>
          )}
        </div>
      )}
      <NotImportedDialog
        open={listOpen}
        items={job.notImported}
        overflow={overflow}
        onClose={() => setListOpen(false)}
        returnFocusTo={opener.current}
      />
    </div>
  );
}
